/* eslint-disable no-console */
import {
  ChevronDownIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  useMediaQuery,
} from '@mui/material';
import useChatStore from 'Components/Chat/store';
import { MAX_MODELS_SELECTION } from 'Constants/models';
import CommonModal from 'Features/CreateTemplatesForm/Components/CommonModal';
import CapabilitiesContainer from 'Features/ModelsModal/components/capabilitiesContainer';
import FiltersContainer from 'Features/ModelsModal/components/filtersContainer';
import ModelsContainer from 'Features/ModelsModal/components/modelsContainer';
import TabsContainer from 'Features/ModelsModal/components/tabs';
import useCommonFunctionsModels from 'Features/ModelsModal/hooks/useCommonFunctionsModels';
import useModelsAndCapabilities from 'Features/ModelsModal/hooks/useModelsAndCapabilities';
import useModelsModalStore from 'Features/ModelsModal/store';
import { inject, observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

function ConfirmButton({ onClick }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        backgroundColor: '#464BBA',
        color: '#FFFFFF',
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px',
        minWidth: '140px',
        '&:hover': {
          backgroundColor: '#3B40A0',
        },
        '@media (max-width:900px)': {
          padding: '4px',
          minWidth: '120px',
        },
        '@media (max-width:600px)': {
          padding: '4px',
          minWidth: '100px',
          fontSize: '14px',
        },
      }}
    >
      <CheckIcon /> Confirm
    </Button>
  );
}

const ModelsModal = ({
  setSelectedModels,
  selectedCapabilities,
  onCapabilities,
  store,
  open,
  onClose,
  // Props from your branch:
  nocapabilities,
  selectedModels: propSelectedModels,
  themeValue,
  nodisclaimer,
  handleCombos,
  maxModels,
  allowNullSelection,
}) => {
  // COMMON MODAL STORE
  const { layout, tab, favorites, setRefetch, refetch, setSearch } =
    useModelsModalStore();

  // CHAT STORE
  const {
    setModelsList,
    addResponseModels,
    setAddResponseModels,
    replaceResponse,
    selectedModels: chatStoreSelectedModels,
  } = useChatStore((state) => state);

  // Determine which selectedModels to use:
  const selectedModels = propSelectedModels || chatStoreSelectedModels;
  const [temporarySelectedModels, setTemporarySelectedModels] = useState([]);

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Pricing Method
  const [showPerMessagePricing, setShowPerMessagePricing] = useState(
    store.profile.pricing_method === 'perMessage'
  );
  const [pricingMethodChanged, setPricingMethodChanged] = useState(false);

  const handlePricingMethodChange = (newValue) => {
    setShowPerMessagePricing(newValue);
    setPricingMethodChanged(
      (store.profile.pricing_method === 'perMessage') !== newValue
    );
  };

  useEffect(() => {
    // Synchronize temporary models when modal opens or selectedModels change
    if (open) {
      setTemporarySelectedModels(selectedModels);
    }
  }, [open, selectedModels]);

  const hasChanges = useMemo(() => {
    // A simple JSON compare or you could do a more robust check if needed
    return (
      JSON.stringify(temporarySelectedModels) !== JSON.stringify(selectedModels)
    );
  }, [temporarySelectedModels, selectedModels]);

  // GET MODELS AND CAPABILITIES DATA
  const { models, capabilities, isLoading, setModels } =
    useModelsAndCapabilities({
      store,
      setModelsList,
    });

  // COMMON FUNCTIONS
  const {
    isSelected,
    disabled,
    selected,
    isActive,
    getCompability,
    getIcon,
    getUncompability,
  } = useCommonFunctionsModels({
    capabilities,
    modelsSelected: temporarySelectedModels,
    capabilitiesSelected: selectedCapabilities,
    addResponseModels,
    selectedModels,
  });

  // Handle model selection
  const handleSelectModels = (models) => {
    setSearch('');
    const updatedModels = models.filter((item) => item && item._id);
    setSelectedModels(updatedModels);
  };

  const getMaxModelsLabel = () => {
    const max = maxModels || MAX_MODELS_SELECTION;
    return replaceResponse ? max - 1 : max;
  };

  const handleSelect = (model) => {
    if (isSelected(model)) {
      const updatedModels = temporarySelectedModels.filter(
        (item) => item._id !== model._id
      );
      if (updatedModels.length > 0) {
        setTemporarySelectedModels(updatedModels);
      } else {
        if (allowNullSelection) {
          setTemporarySelectedModels([]);
        } else {
          toast('You must have at least one model selected', {
            icon: '⚠️',
          });
        }
      }
    } else {
      const maxModelsSelection = maxModels || MAX_MODELS_SELECTION;
      if (temporarySelectedModels.length < maxModelsSelection) {
        const updatedModels = [...temporarySelectedModels, model];
        setTemporarySelectedModels(updatedModels);
      } else {
        toast(
          `You only can select up to ${getMaxModelsLabel()} LLMs simultaneously`,
          {
            icon: '⚠️',
          }
        );
      }
    }
  };

  const handleConfirm = async () => {
    setSearch('');
    // Handle pricing method change if needed
    if (pricingMethodChanged) {
      const newMethod = showPerMessagePricing ? 'perMessage' : 'perWord';
      updatePricingMethod(newMethod).catch((error) => {
        console.error('Failed to update pricing method:', error);
      });
    }

    // Proceed with model selection regardless of pricing update
    if (typeof handleCombos === 'function') {
      handleCombos(temporarySelectedModels);
    } else {
      handleSelectModels(temporarySelectedModels);
    }
    onClose();
  };

  const handleAttemptClose = () => {
    setSearch('');
    if (hasChanges) {
      setShowDiscardDialog(true);
    } else {
      // If no changes, just close normally
      onClose();
    }
  };

  const handleDiscardAndExit = () => {
    setSearch('');
    // Reset the temporary selection to the original
    setTemporarySelectedModels(selectedModels);
    setShowDiscardDialog(false);
    setAddResponseModels(false);
    onClose();
  };

  // Called if user wants to continue editing
  const handleContinueEditing = () => {
    setShowDiscardDialog(false);
  };

  // Handle capability selection
  const handleSelectCapability = (capability) => {
    if (isActive(capability)) {
      // Already active => remove it
      const updatedCaps = selectedCapabilities?.filter(
        (item) => item._id !== capability._id
      );
      onCapabilities(updatedCaps);
    } else {
      // Not active => add it
      onCapabilities([...selectedCapabilities, capability]);
    }
  };

  // Local update of the model's favorite state
  const onStaredLocal = (model) => {
    if (model.favorite) {
      // Unfavorite
      const modelsArray = models.map((item) =>
        item._id === model._id ? { ...item, favorite: false } : item
      );
      setModels(modelsArray);
    } else {
      // Mark as favorite
      const modelsArray = models.map((item) =>
        item._id === model._id ? { ...item, favorite: true } : item
      );
      setModels(modelsArray);
    }
  };

  // Handle toggling a model's favorite state
  const handleToggleStar = async (model) => {
    onStaredLocal(model);
    try {
      if (!model.favorite) {
        // If it was NOT favorite, we are adding it
        await store.api.post('/v2/models/favorite', { modelId: model._id });
        toast.success('Model added to favorites');
      } else {
        // If it was already favorite, we are removing it
        await store.api.delete(`/v2/models/favorite/${model._id}`);
        toast.success('Model removed from favorites');
      }
      // If currently on "favorites" tab, force a refetch so the UI updates
      if (favorites) {
        setRefetch(refetch ? 0 : 1);
      }
    } catch (error) {
      console.error('Error adding model to favorites:', error);
      toast.error('Error adding model to favorites');
    }
  };

  const updatePricingMethod = async (method) => {
    try {
      const res = await store.api.post(`/user/update-user`, {
        pricing_method: method,
      });

      if (res.status === 200) {
        // Update both local storage and store
        const profile = JSON.parse(localStorage.getItem('profile'));
        profile.pricing_method = method;
        localStorage.setItem('profile', JSON.stringify(profile));
        store.profile.pricing_method = method;

        const newMode = method === 'perMessage';
        const svgIconBase64 = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjciIGhlaWdodD0iMzQiIHZpZXdCb3g9IjAgMCAyNyAzNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjYuNSA4LjVIMTkuMjc3NkwyMy42MTA5IDBIMTIuMDU1OEwwLjUgMjIuNjY2NEg5LjE2NjY3VjM0TDI2LjUgOC41WiIgZmlsbD0iaW5oZXJpdCIvPjwvc3ZnPg==`;

        const customToastOptions = {
          style: {
            background: newMode ? '#FFEFE5' : '#E1E1E5',
            color: '#000',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            width: window.innerWidth < 600 ? '100%' : 'auto',
            maxWidth: window.innerWidth < 600 ? '100%' : '30%',
            minWidth: window.innerWidth < 600 ? '100%' : '20%',
          },
          icon: (
            <img
              src={svgIconBase64}
              alt="Lightning Icon"
              width={window.innerWidth < 600 ? '20' : '24'}
              height={window.innerWidth < 600 ? '20' : '24'}
              style={{
                marginRight: '8px',
                filter: newMode
                  ? 'invert(34%) sepia(100%) saturate(1500%) hue-rotate(-14deg) brightness(180%) contrast(90%) opacity(70%)'
                  : 'invert(0%) sepia(0%) saturate(0%) hue-rotate(204deg) brightness(100%) contrast(10%)',
              }}
            />
          ),
          duration: 3000,
          position: window.innerWidth < 600 ? 'top-center' : 'top-right',
        };

        const titleStyle = {
          fontWeight: 700,
          fontSize: '16px',
          marginBottom: '4px',
          color: newMode ? '#F2A474' : '#575557',
          fontFamily: 'Figtree, sans-serif',
        };

        const subtitleStyle = {
          fontSize: '14px',
          color: '#6B6B6B',
        };

        // Show appropriate toast message
        if (newMode) {
          toast(
            <div>
              <div style={titleStyle}>Pricing per Message – Activated</div>
              <div style={subtitleStyle}>
                Fixed cost per message, regardless of length
              </div>
            </div>,
            customToastOptions
          );
        } else {
          toast(
            <div>
              <div style={titleStyle}>Pricing per Message – Deactivated</div>
              <div style={subtitleStyle}>
                Cost now varies based on conversation length
              </div>
            </div>,
            customToastOptions
          );
        }
        return true;
      }

      // Error case styling
      const errorToastOptions = {
        style: {
          background: '#FEE2E2',
          color: '#991B1B',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '10px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          width: window.innerWidth < 600 ? '100%' : 'auto',
          maxWidth: window.innerWidth < 600 ? '100%' : '30%',
          minWidth: window.innerWidth < 600 ? '100%' : '20%',
        },
        duration: 3000,
        position: window.innerWidth < 600 ? 'top-center' : 'top-right',
      };

      toast.error('Error updating pricing method', errorToastOptions);
      return false;
    } catch (error) {
      console.error('Error updating pricing method:', error);
      toast.error('Error updating pricing method', {
        style: {
          background: '#FEE2E2',
          color: '#991B1B',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '10px',
          padding: '16px',
        },
        duration: 3000,
        position: window.innerWidth < 600 ? 'top-center' : 'top-right',
      });
      return false;
    }
  };

  const isMobile = useMediaQuery('(max-width:600px)');

  // NEW STATE: track whether the header is expanded or collapsed.
  const [isExpanded, setIsExpanded] = useState(true);

  const TabPaneRender = {
    0: (
      <ModelsContainer
        layout={layout}
        models={models}
        selected={selected}
        disabled={disabled}
        handleChange={handleSelect}
        isLoading={isLoading}
        handleToggleStar={handleToggleStar}
        showPerMessagePricing={showPerMessagePricing}
      />
    ),
    1: (
      <>
        {!nocapabilities && (
          <CapabilitiesContainer
            getIcon={getIcon}
            isActive={isActive}
            capabilities={capabilities}
            getCompability={getCompability}
            modelsSelected={temporarySelectedModels}
            getUncompability={getUncompability}
            handleCapabilities={handleSelectCapability}
          />
        )}
      </>
    ),
  };

  return (
    <>
      <CommonModal
        noPaddingX
        open={open}
        maxWidth="xl"
        fullWidth
        maxHeight="100%"
        withCloseButton
        handleClose={handleAttemptClose}
        className="p-0 m-0 -mx-2 -my-6 md:p-4 md:m-4 md:mx-0 md:my-0"
      >
        {/* Wrapping container marked as relative to position the toggle button */}
        <div className="h-full w-full relative overflow-hidden flex flex-col font-figtree">
          {/* Toggle button positioned in the top left corner */}
          {isMobile && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="absolute top-0 left-0 z-10"
            >
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform duration-300 ${!isExpanded ? 'rotate-180' : ''
                  }`}
              />
            </button>
          )}

          {/* The header block will only render when expanded */}
          {isExpanded && (
            <div className="mt-8">
              <div className="flex gap-2 items-center text-raisin-black dark:text-crystal-bell mb-4">
                <h1 className="font-bold text-3xl">Select prompt feature</h1>
                {!nodisclaimer && (
                  <>
                    {tab === 0 && (
                      <div className="hidden md:flex flex-col justify-center text-xs font-normal px-4 sm:px-6">
                        {nocapabilities ? (
                          <ul className="list-disc space-y-2">
                            <li className="ml-5">
                              <span className="-ml-1">
                                The LLMs you select below will make up your
                                combo
                              </span>
                            </li>
                            <li className="ml-5 mt-1">
                              <span className="-ml-1">
                                You can select up to {getMaxModelsLabel()} LLMs
                                simultaneously
                              </span>
                            </li>
                          </ul>
                        ) : (
                          <ul className="list-disc space-y-2">
                            <li className="ml-5">
                              <span className="-ml-1">
                                Select from our preset LLM combos based on your
                                task, or pick up to {getMaxModelsLabel()} LLMs
                                for this chat
                              </span>
                            </li>
                            <li className="ml-5 mt-1">
                              <span className="-ml-1">
                                Need a custom combo? Head to{' '}
                                <a
                                  className="font-semibold text-nue-blue underline"
                                  href="/user-settings#personalizeAI"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  settings
                                </a>{' '}
                                to create or edit your own
                              </span>
                            </li>
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              {nocapabilities ? null : (
                <Box
                  display="flex"
                  flexDirection={isMobile ? 'column' : 'row'}
                  alignItems="center"
                  width="100%"
                >
                  <TabsContainer nocapabilities={nocapabilities} />
                  <Box
                    ml={isMobile ? 0 : 'auto'}
                    width={isMobile ? '100%' : 'auto'}
                  >
                    <div className="hidden sm:block">
                      <ConfirmButton onClick={handleConfirm} />
                    </div>
                  </Box>
                </Box>
              )}
            </div>
          )}
          {/* TABS section (remains always visible) */}

          <Divider flexItem />
          {tab === 0 && (
            <FiltersContainer
              nocapabilities={nocapabilities}
              selectedModels={temporarySelectedModels}
              handleSelectModel={handleSelect}
              models={models}
              themeValue={themeValue}
              handleConfirm={handleConfirm}
              actionButton={
                nocapabilities ? (
                  <Box width={isMobile ? '100%' : 'auto'}>
                    <ConfirmButton onClick={handleConfirm} />
                  </Box>
                ) : null
              }
              showPerMessagePricing={showPerMessagePricing}
              setShowPerMessagePricing={handlePricingMethodChange}
              isDisabled={disabled}
            />
          )}
          {/* Filters (only on tab 0) */}
          <div
            className={`${isMobile ? 'h-screen' : 'flex-1'} overflow-y-scroll`}
            style={isMobile ? { height: '100vh', width: '100vw' } : {}}
          >
            {TabPaneRender[tab]}
          </div>
        </div>
      </CommonModal>
      {showDiscardDialog &&
        <Dialog
          open={showDiscardDialog}
          onClose={handleContinueEditing}
          PaperProps={{
            sx: {
              minHeight: 'initial',
              backgroundColor: themeValue === 'dark' ? '#252425' : '#F7F7F8',
              padding: 0,
              borderRadius: 6,
            },
            elevation: 0,
          }}
        >
          <DialogTitle
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '1.5rem',
              color: themeValue === 'dark' ? '#EFEFEF' : '#211E21',
              mb: 1,
            }}
          >
            Models Not Saved!
          </DialogTitle>

          <DialogContent sx={{ paddingBottom: '8px' }}>
            <DialogContentText
              sx={{
                textAlign: 'center',
                fontSize: '1rem',
                color: 'text.primary',
                maxWidth: 380,
              }}
            >
              Exiting now will discard any new changes to your current models
              selection. Are you sure you want to proceed?
            </DialogContentText>
          </DialogContent>

          <DialogActions
            sx={{
              justifyContent: 'space-between',
              padding: '18px 24px',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleDiscardAndExit}
              startIcon={<TrashIcon className="size-4" />}
              sx={{ lineHeight: 1.2 }}
            >
              Discard and exit
            </Button>
            <Button
              color="primary"
              variant="filled"
              onClick={handleContinueEditing}
              startIcon={<PencilSquareIcon className="size-4" />}
              sx={{ lineHeight: 1.2 }}
            >
              Continue selecting
            </Button>
          </DialogActions>
        </Dialog>}
    </>
  );
};

export default inject('store')(observer(ModelsModal));
